import ElementComponent from "elements/base/konva-component";
import { BoardContext, getElementProvider } from "elements/index";
import { getTemplateData } from "frontend/api";
import { Footer } from "frontend/canvas-controls/footer";
import { fitToStage } from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { ZoomControls } from "frontend/canvas-controls/zoom-controls/zoom-controls";
import { getElementTypeForId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import { MiniKonvaStage } from "frontend/konva-stage-types";
import useStateValue from "frontend/state/value";
import { calcPixelRatio, getAreaToExport, getSnapshotBasic } from "frontend/utils/export-utils";
import * as MathUtils from "frontend/utils/math-utils";
import { useSetAtom } from "jotai";
import Konva from "konva";
import { KonvaEventObject } from "konva/types/Node";
import { Vector2d } from "konva/types/types";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import { Layer, Stage } from "react-konva";
import consts from "shared/consts";
import { Point } from "shared/datamodel/schemas/canvas-element";
import { canvasElementPrefix, canvasMetadataPrefix } from "shared/util/utils";
import { miniStageRefAtom } from "state-atoms";
import { CanvasProvider } from "./canvas-context";
import { CanvasElement } from "./canvas-element";
import FrameBackground from "./elements/frame/frame-background";
import { MondayItemManagerProvider } from "./integrations/components/monday-item-manager-context";
import { noop } from "utils/fn-utils";

export default function MiniStage({
  width,
  height,
  templateId,
  onLoad,
  templateData,
  showFooter = true,
}: {
  width: number;
  height: number;
  templateId: string;
  onLoad?: (loaded: boolean) => void;
  templateData?: any;
  showFooter?: boolean;
}) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [templateElements, setTemplateElements] = useState<Array<[string, any]>>([]);
  const [templatesMetadata, setTemplatesMetadata] = useState<Record<string, any>>({});
  const setMiniStageRefAtom = useSetAtom(miniStageRefAtom);

  const ref = useRef<MiniKonvaStage>(null);
  const layerRef = useRef<any>(null);
  const mouseRef = useRef<any>({ isDown: false });
  const loaded = useRef(false);

  const [appState] = useStateValue();

  useEffect(() => {
    async function loadTemplateData() {
      let elements: Array<[string, any]> = [];
      const metadata: Record<string, any> = {};

      templateData ??= templateId ? (await getTemplateData(templateId))?.data : {};

      for (const [id, value] of Object.entries(templateData)) {
        if (id.startsWith(canvasElementPrefix)) {
          const elementId = id.slice(canvasElementPrefix.length);
          elements.push([elementId, value]);
        } else if (id.startsWith(canvasMetadataPrefix)) {
          const elementId = id.slice(canvasMetadataPrefix.length);
          metadata[elementId] = value;
        }
      }

      elements.sort((a, b) => a[1].zIndexLastChangeTime - b[1].zIndexLastChangeTime);
      elements = elements.map(([id, element]) => [id, { ...element, link: null }]);
      elements.length > 0 && setTemplateElements(elements);

      const formattedMetadata: { [key: string]: any } = {};

      for (const key in metadata) {
        if (metadata.hasOwnProperty(key)) {
          const value = metadata[key];
          const newKey = key.split("-")[1] as any;
          formattedMetadata[newKey] = value;
        }
      }

      metadata && setTemplatesMetadata(formattedMetadata);
    }

    loadTemplateData();
  }, [templateId, templateData]);

  const layer = ref.current?.getStage()?.getLayers()[0];

  useEffect(() => {
    let requestAnimationFrameId: number | null = null;
    let requestAnimationFrameId2: number | null = null;
    if (
      layer &&
      layer.children &&
      layer.children.length > 0 &&
      templateElements.length > 0 &&
      loaded.current === false
    ) {
      // Wait for elements to be fully rendered before calculating bounds
      // Double requestAnimationFrame ensures:
      // - Frame 1: React has rendered elements to DOM
      // - Frame 2: Browser has painted and Konva has calculated element bounds
      // This is necessary because getClientRect() needs elements to be painted to return correct values
      requestAnimationFrameId = requestAnimationFrame(() => {
        requestAnimationFrameId2 = requestAnimationFrame(() => {
          if (!ref.current || loaded.current) return;
          const viewBox = fitToStage(ref);
          if (!viewBox) return; // Retry on next render if bounds aren't ready
          setPosition({ x: viewBox.x, y: viewBox.y });
          setScale(viewBox.scale);
          onLoad && onLoad(true);
          loaded.current = true;
        });
      });
    }
    return () => {
      if (requestAnimationFrameId) cancelAnimationFrame(requestAnimationFrameId);
      if (requestAnimationFrameId2) cancelAnimationFrame(requestAnimationFrameId2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer?.children.length, templateElements.length]);

  useEffect(() => {
    if (ref.current) {
      ref.current.getSnapshotImage = getSnapshotFunction;
    }
    setMiniStageRefAtom(ref);
  }, [ref.current, layerRef.current]);

  async function getSnapshotFunction({
    withWatermark,
    quality,
    elementId,
    scale,
    position,
    maxSize,
    padding = 40,
    whiteBackground = true,
  }: {
    withWatermark: boolean;
    elementId?: string;
    quality: number;
    scale: number;
    position: Point;
    maxSize?: number;
    padding?: number;
    whiteBackground?: boolean;
  }) {
    const canvasLayer = layerRef.current;
    if (canvasLayer == null) return;
    // 1. area is the rect in canvas coordinates that contains all elements, or the frame supplied
    let area = getAreaToExport(canvasLayer, elementId);

    // TODO: if area.width or height == 0, abort; don't export empty canvas
    area = MathUtils.padRect(area, padding);

    // 2. Konva requires us to adjust for the stage's current scale and position when exporting
    const adjustedArea = {
      x: area.x * scale + position.x,
      y: area.y * scale + position.y,
      width: area.width * scale,
      height: area.height * scale,
    };

    // 3. And we also have to choose pixel ratio, which really determines final size and quality.
    //    If we choose window.devicePixelRatio we get a match between screen pixels and canvas pixels.
    //    But maximum value allowed is by quality setting and browser limits.
    const minFinalSize = 512;
    const maxFinalSize = maxSize ?? 32767;
    const pixelRatio = calcPixelRatio(adjustedArea, minFinalSize, maxFinalSize, quality);

    const config = {
      ...adjustedArea,
      pixelRatio,
    };
    return getSnapshotBasic(canvasLayer, area, withWatermark, config, whiteBackground);
  }

  function moveStagePosition(dx: number, dy: number) {
    const dragDistanceScale = 0.75;
    setPosition((pos: Point) => {
      return {
        x: pos.x - dragDistanceScale * dx,
        y: pos.y - dragDistanceScale * dy,
      };
    });
  }

  function zoomStage(stage: Konva.Stage, dy: number, mode: "mouse" | "viewport-center") {
    const oldScale = stage.scaleX();
    const newScale = MathUtils.clamp(oldScale - dy * 0.01, consts.STAGE_ZOOM.MIN, consts.STAGE_ZOOM.MAX);
    // This should be canvas element center, but since we stretch the canvas over the window
    // I just take window center
    const point =
      mode == "mouse"
        ? (stage.getPointerPosition() as Vector2d)
        : {
            x: 0.5 * width,
            y: 0.5 * height,
          };
    const centerX = (point.x - position.x) / scale;
    const centerY = (point.y - position.y) / scale;
    const offsetX = centerX * (newScale - scale);
    const offsetY = centerY * (newScale - scale);
    const newPosition = {
      x: position.x - offsetX,
      y: position.y - offsetY,
    };
    setScale(newScale);
    setPosition(newPosition);
  }

  function handleWheelScroll(e: any) {
    e.evt.preventDefault();
    const stage = ref.current;
    if (!stage) {
      return;
    }
    const physicalWheel = Math.abs(e.evt.wheelDelta % 120) == 0;
    if (physicalWheel || e.evt.ctrlKey || e.evt.metaKey) {
      // The numerical values in the section were chosen to give a nice user experience
      // They are pretty arbitrary
      let dy = e.evt.deltaY;
      if (e.evt.deltaMode === WheelEvent.DOM_DELTA_LINE) dy = dy * 8;
      else if (e.evt.deltaMode === WheelEvent.DOM_DELTA_PAGE) dy = dy * 24;
      dy = MathUtils.clamp(dy, -24, 24);
      const isPinchGesture = e.evt.wheelDelta != e.evt.deltaY * -3;
      const scaleFactor = isPinchGesture ? 5 : 1;
      const finalScaleChange = (scaleFactor * stage.scaleX() * dy) / 5;
      zoomStage(stage, finalScaleChange, "mouse");
    } else {
      moveStagePosition(e.evt.deltaX, e.evt.deltaY);
    }
  }

  function handleMouseDown(e: KonvaEventObject<MouseEvent>) {
    e.target.preventDefault();
    if (ref.current) {
      ref.current.container().style.cursor = "grabbing";
    }
    mouseRef.current.isDown = true;
  }

  function handleMouseUp(e: KonvaEventObject<MouseEvent>) {
    e.target.preventDefault();
    if (ref.current) {
      ref.current.container().style.cursor = "grab";
    }
    mouseRef.current.isDown = false;
  }

  function handleMouseMove(e: KonvaEventObject<MouseEvent>) {
    if (ref && ref.current && mouseRef.current.isDown) {
      moveStagePosition(-e.evt.movementX, -e.evt.movementY);
    }
  }

  function stageStyle(): CSSProperties {
    const backgroundColor = "white";
    const baseSize = 25;
    if (scale >= 1) {
      const size = baseSize * scale;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      const dotSize = 1.1 * scale;
      return {
        backgroundColor,
        backgroundImage: `radial-gradient(#E5E8EA ${dotSize}px, transparent ${dotSize}px)`,
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else if (scale >= 0.5) {
      const size = baseSize * scale * 2;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      return {
        backgroundColor,
        backgroundImage: "radial-gradient(#E5E8EA 1.1px, transparent 1.1px)",
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else if (scale >= 0.25) {
      const size = baseSize * scale * 4;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      return {
        backgroundColor,
        backgroundImage: "radial-gradient(#E5E8EA 1.1px, transparent 1.1px)",
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else if (scale >= 0.1) {
      const size = baseSize * scale * 8;
      const x = (position.x % size) + size / 2;
      const y = (position.y % size) + size / 2;
      return {
        backgroundColor,
        backgroundImage: "radial-gradient(#E5E8EA 1.1px, transparent 1.1px)",
        backgroundSize: `${size}px ${size}px`,
        backgroundPosition: `${x}px ${y}px`,
      };
    } else {
      return {
        backgroundColor,
      };
    }
  }

  function renderElements(elements: Array<[string, any]>) {
    const isLoggedIn = !(!appState.user || appState.user.isGuest);
    const context: BoardContext = {
      documentId: "",
      boardId: 0,
      reflect: { subscribe: () => {} } as any,
      isReadOnly: true,
      isLoggedIn,
      selectedElementIds: [],
      setSelectedIds: noop,
      setStageState: noop,
      hiddenElementIds: [],
      editingElementId: null,
      editingElementLinkId: null,
      undoRedoStack: undefined,
    };
    return elements.map(([elementId, elementData], _, allElements) => {
      const elementType = getElementTypeForId(elementId);
      const provider = getElementProvider(elementType);
      if (provider) {
        if (provider.isFFEnabled()) {
          // if provider exists it means it support the new infra
          return (
            <ElementComponent
              key={elementId}
              id={elementId}
              type={elementType}
              context={context}
              elementData={elementData}
              allElementsData={allElements}
              onResize={noop}
            />
          );
        } else {
          return null;
        }
      }

      return (
        <CanvasElement
          key={elementId}
          uniqueId={elementId}
          elementData={elementData}
          allElementsData={allElements}
          onResize={noop}
          onChangeElement={noop}
          isSelected={false}
          drawOutlineAroundElements={false}
          isEditing={false}
          isEditingLink={false}
          isFrameHighlighted={false}
          onElementsMutationEnded={noop}
          isSelectable={false}
          metaData={templatesMetadata}
          isReadOnly={true}
          patchCanvasElement={noop}
          patchAnything={noop}
          documentId={""}
        />
      );
    });
  }

  function renderFramesBackgrounds() {
    const frameElements = templateElements.filter(([_id, element]) => element?.type === consts.CANVAS_ELEMENTS.FRAME);

    return frameElements.map(([id, element]) => <FrameBackground key={id} uniqueId={id} frame={element} />);
  }

  function onHover() {
    if (ref.current) {
      ref.current.container().style.cursor = "grab";
    }
  }

  function renderStage() {
    return (
      <React.Fragment>
        <div onMouseOver={onHover} onMouseEnter={onHover}>
          <Stage
            ref={ref}
            width={width}
            height={height}
            x={position.x}
            y={position.y}
            scaleX={scale}
            scaleY={scale}
            onWheel={handleWheelScroll}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={stageStyle()}
          >
            <CanvasProvider appState={appState}>
              <MondayItemManagerProvider documentId="">
                <Layer name="Elements" ref={layerRef}>
                  {renderFramesBackgrounds()}
                  {renderElements(templateElements)}
                </Layer>
              </MondayItemManagerProvider>
            </CanvasProvider>
          </Stage>
        </div>
      </React.Fragment>
    );
  }

  function renderFooter() {
    return (
      <Footer
        customStyle={{
          gridArea: "footer",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <ZoomControls
          scale={scale}
          position={position}
          onZoom={(scale: number, position: Point) => {
            setScale(scale);
            setPosition(position);
          }}
          viewportWidth={width}
          viewportHeight={height}
          showTooltips={false}
          stageRef={ref}
        />
      </Footer>
    );
  }

  return (
    <React.Fragment>
      {renderStage()}
      {showFooter && renderFooter()}
    </React.Fragment>
  );
}
